import { useState, useEffect } from "react";
import { Link } from "react-router";
import { motion } from "motion/react";
import {
  Phone, Mail, MapPin, Clock, Send, CheckCircle, MessageSquare,
  ArrowRight, Headphones, Shield, ChevronRight
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { enquiriesService } from "../../lib/services/enquiriesService";
import { servicesService, type Service } from "../../lib/services/servicesService";
import { Loader2 } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

import { SEO } from "./SEO";

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service: "", message: "",
  });
  const [services, setServices] = useState<Service[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    servicesService.list()
      .then((data) => {
        const active = data.filter(s => s.status !== "Inactive");
        setServices(active.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder)));
      })
      .catch(console.error);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const date = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // 1. Save to Appwrite
      await enquiriesService.create({
        ...formData,
        status: "new",
        date
      });

      setSubmitted(true);
      toast.success("Thank you! We'll be in touch within 24 hours.");
    } catch (error: any) {
      console.error("Error submitting enquiry:", error);
      toast.error(`Error: ${error.message || "Something went wrong. Please try again later."}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      <SEO
        title="Contact Us | Professional HVAC & Mechanical Engineering Services"
        description="Get in touch with Solace SMART Solutions for expert HVAC, refrigeration, and electrical engineering inquiries. Request a free quote today."
      />
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-[#1a2d50] via-[#1f3561] to-[#243d6a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-[#E9A319] rounded-full blur-3xl" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center justify-center gap-2 text-sm mb-6 w-full">
            <Link to="/" className="text-white/50 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/30" />
            <span className="text-[#E9A319]" style={{ fontWeight: 600 }}>Contact Us</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-5xl mt-3 mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            Get in Touch With Our Team
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-blue-100 max-w-2xl mx-auto text-lg" style={{ lineHeight: 1.8 }}>
            Have an enquiry or need a quote? Fill out the form below and our expert team will get back to you promptly with an optimum cost solution.
          </motion.p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-28 relative z-20">
            {[
              { icon: Phone, title: "Call Us", info: "+91 88008-78095", sub: "+91 88755-55358", href: "tel:+918800878095", color: "text-[#1a2d50] bg-[#1a2d50]/10" },
              { icon: Mail, title: "Email Us", info: "Connect@Solacesmart.com", sub: "Response within 24hrs", href: "mailto:Connect@Solacesmart.com", color: "text-green-600 bg-green-50" },
              { icon: MapPin, title: "Head Office", info: "Jaipur, Rajasthan", sub: "B12, Pratap Plaza, Pratap Nagar", href: "#offices", color: "text-[#E9A319] bg-[#E9A319]/10" },
              { icon: Headphones, title: "Support Hours", info: "Mon – Sat", sub: "9:00 AM – 6:00 PM", href: "#", color: "text-red-500 bg-red-50" },
            ].map((item) => (
              <motion.a
                key={item.title}
                variants={fadeUp}
                href={item.href}
                className="bg-white rounded-2xl p-6 shadow-xl border border-[#1a2d50]/5 hover:shadow-2xl transition-all group text-center"
              >
                <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-7 h-7" />
                </div>
                <h3 className="text-foreground mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{item.title}</h3>
                <p className="text-foreground text-sm" style={{ fontWeight: 600 }}>{item.info}</p>
                <p className="text-muted-foreground text-xs mt-1">{item.sub}</p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Sidebar */}
      <section className="pb-24 overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <h2 className="text-3xl text-[#1a2d50] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                Request a Free Quote
              </h2>
              <p className="text-muted-foreground mb-8" style={{ lineHeight: 1.8 }}>
                Tell us about your requirements and we'll prepare an optimum cost solution tailored for you. No pressure, no obligation — just expert advice.
              </p>

              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 rounded-2xl p-12 text-center border border-green-100"
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl text-foreground mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                    Message Sent Successfully!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out. Our team will contact you within 24 hours to discuss your requirements.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", service: "", message: "" }); }}
                    className="text-accent flex items-center gap-2 mx-auto hover:gap-3 transition-all"
                    style={{ fontWeight: 600 }}
                  >
                    Send Another Message <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-foreground mb-2" style={{ fontWeight: 600 }}>Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full px-5 py-3.5 rounded-xl bg-[#f8f6f1] border border-[#1a2d50]/10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E9A319]/40 focus:border-[#E9A319] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground mb-2" style={{ fontWeight: 600 }}>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full px-5 py-3.5 rounded-xl bg-[#f8f6f1] border border-[#1a2d50]/10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E9A319]/40 focus:border-[#E9A319] transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-foreground mb-2" style={{ fontWeight: 600 }}>Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full px-5 py-3.5 rounded-xl bg-[#f8f6f1] border border-[#1a2d50]/10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E9A319]/40 focus:border-[#E9A319] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground mb-2" style={{ fontWeight: 600 }}>Service Required *</label>
                      <select
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-5 py-3.5 rounded-xl bg-[#f8f6f1] border border-[#1a2d50]/10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E9A319]/40 focus:border-[#E9A319] transition-all appearance-none"
                      >
                        <option value="">Select a service</option>
                        {services.map((s) => (
                          <option key={s.$id} value={s.title}>{s.title}</option>
                        ))}
                        <option value="Other / Not Sure">Other / Not Sure</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground mb-2" style={{ fontWeight: 600 }}>Project Details *</label>
                    <textarea
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Describe your requirements — type of facility, approximate area, specific needs, budget range, location..."
                      rows={5}
                      className="w-full px-5 py-3.5 rounded-xl bg-[#f8f6f1] border border-[#1a2d50]/10 text-sm focus:outline-none focus:ring-2 focus:ring-[#E9A319]/40 focus:border-[#E9A319] transition-all resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto bg-[#E9A319] text-white px-10 py-4 rounded-full hover:bg-[#d4920f] transition-all shadow-lg shadow-[#E9A319]/25 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ fontWeight: 600 }}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {loading ? "Sending..." : "Send Enquiry"}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-8"
            >
              {/* Office Locations */}
              <div id="offices" className="bg-[#f8f6f1] rounded-2xl p-6 border border-[#1a2d50]/10 space-y-5">
                <h3 className="text-[#1a2d50] flex items-center gap-2 mb-5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                  <MapPin className="w-5 h-5 text-[#E9A319]" /> Our Offices
                </h3>
                {[
                  { state: "Rajasthan", address: "B12, Pratap Plaza, Pratap Nagar, Jaipur" },
                  { state: "Uttar Pradesh", address: "B-488, XU III, Greater Noida" },
                  { state: "Gujarat", address: "14, Sun Enclave, Halol, Gujrat" },
                ].map((office) => (
                  <div key={office.state} className="border-b border-[#1a2d50]/5 last:border-0 pb-4 last:pb-0">
                    <p className="text-[#1a2d50] text-sm" style={{ fontWeight: 700 }}>{office.state}</p>
                    <p className="text-muted-foreground text-sm mt-0.5">{office.address}</p>
                  </div>
                ))}
              </div>

              {/* Business Hours */}
              <div className="bg-[#f8f6f1] rounded-2xl p-6 border border-[#1a2d50]/10">
                <h3 className="text-[#1a2d50] flex items-center gap-2 mb-5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                  <Clock className="w-5 h-5 text-[#E9A319]" /> Business Hours
                </h3>
                <div className="space-y-3 text-sm">
                  {[
                    { day: "Monday - Saturday", time: "9:00 AM - 6:00 PM" },
                    { day: "Sunday", time: "Closed" },
                    { day: "Emergency Support", time: "Available on Call" },
                  ].map((item) => (
                    <div key={item.day} className="flex justify-between">
                      <span className="text-foreground" style={{ fontWeight: 500 }}>{item.day}</span>
                      <span className={item.day === "Emergency Support" ? "text-[#E9A319]" : "text-muted-foreground"} style={{ fontWeight: item.day === "Emergency Support" ? 600 : 400 }}>
                        {item.time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Contact */}
              <div className="bg-[#1a2d50] rounded-2xl p-6 text-white">
                <h3 className="flex items-center gap-2 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                  <MessageSquare className="w-5 h-5 text-[#E9A319]" /> Need Urgent Help?
                </h3>
                <p className="text-blue-100 text-sm mb-5" style={{ lineHeight: 1.7 }}>
                  Call us directly for urgent project requirements or technical assistance.
                </p>
                <a
                  href="tel:+918800878095"
                  className="inline-flex items-center gap-2 bg-[#E9A319] text-white px-6 py-3 rounded-full hover:bg-[#d4920f] transition-all w-full justify-center text-sm"
                  style={{ fontWeight: 600 }}
                >
                  <Phone className="w-4 h-4" /> +91 88008-78095
                </a>
              </div>

              {/* Quality Guarantee */}
              <div className="bg-[#E9A319]/5 rounded-2xl p-6 border border-[#E9A319]/20">
                <div className="flex items-start gap-4">
                  <Shield className="w-10 h-10 text-[#E9A319] shrink-0" />
                  <div>
                    <h4 className="text-foreground mb-1" style={{ fontWeight: 700 }}>Japanese Quality Standards</h4>
                    <p className="text-muted-foreground text-sm" style={{ lineHeight: 1.7 }}>
                      Every project follows our SQDMC culture — Safety, Quality, Delivery, Morale &amp; Cost — to deliver the utmost solutions.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Regions */}
      <section className="py-20 bg-[#f8f6f1]">
        <div className="max-w-screen-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl text-[#1a2d50] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
            States We Serve
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto" style={{ lineHeight: 1.8 }}>
            We provide HVAC, refrigeration, and electrical solutions across India with primary offices in three states.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              "Rajasthan", "Uttar Pradesh", "Gujarat", "Delhi NCR", "Maharashtra", "Karnataka",
              "Tamil Nadu", "Punjab", "Haryana", "Madhya Pradesh",
            ].map((region) => (
              <span key={region} className="bg-white px-5 py-2.5 rounded-full text-sm text-[#1a2d50] border border-[#1a2d50]/10 shadow-sm" style={{ fontWeight: 500 }}>
                {region}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
